/**
 * Salvos API client. Wraps fetch() calls against /api/salvos and
 * /api/admin/salvos with Cognito bearer tokens.
 *
 * Pass an `accessTokenProvider` when constructing so the service can fetch a
 * fresh token on every request (Cognito access tokens expire in 1h).
 */

import { Salvo, SalvoCrosspoint } from '@mediaconnect-router/shared';
import { API_URL } from '../config/env';

export type AccessTokenProvider = () => string | null | undefined;

export interface CreateSalvoInput {
  name: string;
  description?: string;
  crosspoints: SalvoCrosspoint[];
}

export interface UpdateSalvoInput {
  name?: string;
  description?: string;
  crosspoints?: SalvoCrosspoint[];
}

export interface FireResult {
  salvoId: string;
  applied: number;
  connections: Array<{ sourceId: string; destinationId: string; status: string; timestamp: string }>;
}

export class SalvosService {
  private readonly baseUrl: string;
  private readonly getToken: AccessTokenProvider;

  constructor(baseUrl: string = API_URL, tokenProvider: AccessTokenProvider = () => null) {
    // Strip trailing slash so template strings below are predictable.
    this.baseUrl = baseUrl.replace(/\/$/, '');
    this.getToken = tokenProvider;
  }

  // ----- User endpoints -----

  async list(): Promise<Salvo[]> {
    return this.request<Salvo[]>('/salvos', { method: 'GET' });
  }

  async listShared(): Promise<Salvo[]> {
    return this.request<Salvo[]>('/salvos/shared', { method: 'GET' });
  }

  async create(input: CreateSalvoInput): Promise<Salvo> {
    return this.request<Salvo>('/salvos', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  }

  async update(salvoId: string, input: UpdateSalvoInput): Promise<Salvo> {
    return this.request<Salvo>(`/salvos/${encodeURIComponent(salvoId)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
  }

  async remove(salvoId: string): Promise<void> {
    await this.request<void>(`/salvos/${encodeURIComponent(salvoId)}`, { method: 'DELETE', parseJson: false });
  }

  async publish(salvoId: string): Promise<Salvo> {
    return this.request<Salvo>(`/salvos/${encodeURIComponent(salvoId)}/publish`, { method: 'POST' });
  }

  async fire(salvoId: string): Promise<FireResult> {
    return this.request<FireResult>(`/salvos/${encodeURIComponent(salvoId)}/fire`, { method: 'POST' });
  }

  // ----- Admin endpoints -----

  async listPending(): Promise<Salvo[]> {
    return this.request<Salvo[]>('/admin/salvos/pending', { method: 'GET' });
  }

  async approve(userId: string, salvoId: string): Promise<Salvo> {
    return this.request<Salvo>(
      `/admin/salvos/${encodeURIComponent(userId)}/${encodeURIComponent(salvoId)}/approve`,
      { method: 'POST' },
    );
  }

  async reject(userId: string, salvoId: string, reason?: string): Promise<Salvo> {
    return this.request<Salvo>(
      `/admin/salvos/${encodeURIComponent(userId)}/${encodeURIComponent(salvoId)}/reject`,
      {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {}),
      },
    );
  }

  // ----- Internal -----

  private async request<T>(
    path: string,
    init: RequestInit & { parseJson?: boolean } = {},
  ): Promise<T> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...((init.headers as Record<string, string>) ?? {}),
    };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const parseJson = init.parseJson !== false;
    const res = await fetch(`${this.baseUrl}${path}`, { ...init, headers });
    if (!res.ok) {
      let message = `Request failed: ${res.status}`;
      try {
        const body = await res.json();
        message = body.message ?? body.error ?? message;
      } catch {
        /* non-JSON error body */
      }
      throw new Error(message);
    }
    if (!parseJson) return undefined as unknown as T;
    return (await res.json()) as T;
  }
}
